<template>
  <v-row class="pb-5" v-scroll="onScroll">
    <!-- header -->
    <mini-header :breadcrumbs="breadcrumbs" :hiddenTab="true" :dense="true" :scroll="scroll">
      <template v-slot:main>
        <span class="body-2 grey-700--text font-weight-semibold mr-4">Selecciona el mes y año del reporte</span>
        <!-- month-year picker -->
        <v-menu v-model="dateMenu" :close-on-content-click="false" offset-y left :nudge-bottom="6" transition="slide-y-transition">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field class="period-field" v-on="on" v-bind="attrs" :value="periodLabel" prefix="Fecha" placeholder="mes - año"
              readonly outlined single-line dense hide-details height="40" style="max-width: 270px;" append-icon="mdi-calendar-blank" />
          </template>
          <v-date-picker v-model="selected.period" type="month" no-title locale="es" color="blue-500" :max="maxMonth" @change="onPeriodChange" />
        </v-menu>
      </template>
    </mini-header>
    <!-- end header -->

    <!-- caption: cache update -->
    <v-col cols="12" class="py-0 mt-2 text-right" v-if="report && cacheUpdatedLabel">
      <span class="caption grey-500--text d-block">{{ cacheUpdatedLabel }}</span>
    </v-col>

    <!-- loading -->
    <template v-if="loading">
      <v-col cols="12" class="mt-5 pa-0" v-for="card in 4" :key="`sk-${card}`">
        <v-row class="px-1">
          <v-col cols="12" class="pa-2">
            <v-skeleton-loader style="border-radius: 10px !important;" transition="fade-transition" type="image" />
          </v-col>
        </v-row>
      </v-col>
    </template>

    <template v-else>
      <!-- no period selected yet -->
      <v-col cols="12" v-if="!report && !error">
        <empty-state class="mt-10" type="metrics-branch" id="metrics" :height="320" title="¿Quieres ver el monitor?"
          description="Selecciona un período para ver el reporte" :isFree="true" />
      </v-col>

      <!-- error -->
      <v-col cols="12" v-else-if="error">
        <empty-state class="mt-10" type="metrics-branch" id="metrics" :height="320" title="No pudimos cargar el reporte"
          description="Ocurrió un error al consultar el reporte. Intenta nuevamente." :isFree="true">
          <template v-slot:actions>
            <v-btn color="blue-500" :loading="loading" @click="getData">Reintentar</v-btn>
          </template>
        </empty-state>
      </v-col>

      <!-- report -->
      <v-col class="pa-0 mt-3" v-else>
        <v-col cols="12" class="px-0 py-2" v-for="section in sections" :key="section.key">
          <v-card class="rounded-md overflow-hidden" flat>
            <!-- section header -->
            <div class="d-flex align-center px-5 py-3">
              <v-icon size="18" color="grey-500" class="mr-2">{{ section.icon }}</v-icon>
              <span class="subtitle-2 grey-500--text font-weight-bold">{{ section.title }}</span>
              <v-spacer />
              <v-chip small label :color="section.chipColor" :text-color="section.chipTextColor" class="mr-2">
                <span class="font-weight-semibold mr-1">{{ formatAmount(section.totalDocuments, section.currency, false) }}</span>docs
              </v-chip>
              <v-chip small label color="grey-50" text-color="grey-700" class="font-weight-semibold">
                {{ symbolFor(section.currency) }} {{ section.currency }}
              </v-chip>
            </div>
            <v-divider />

            <!-- table: header stays visible even when empty, so the structure is preserved -->
            <v-simple-table class="no-hover table-not-rounded" :class="{ 'empty-table': !section.hasData }" dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Tipo de documento</th>
                    <th class="text-end" v-for="col in columns" :key="col.key">{{ col.label }}</th>
                  </tr>
                </thead>
                <tbody v-if="section.hasData">
                  <tr v-for="row in section.rows" :key="row.documentType">
                    <td class="body-2 grey-700--text">{{ docLabel(row) }}</td>
                    <td class="text-end body-2 grey-700--text" v-for="col in columns" :key="col.key">
                      {{ col.amount ? `${symbolFor(section.currency)} ${formatAmount(row[col.key], section.currency)}` : formatAmount(row[col.key], section.currency, false) }}
                    </td>
                  </tr>
                  <tr class="total-row">
                    <td class="body-2 grey-700--text font-weight-bold">Total</td>
                    <td class="text-end body-2 grey-700--text font-weight-bold" v-for="col in columns" :key="col.key">
                      {{ col.amount ? `${symbolFor(section.currency)} ${formatAmount(section.totals[col.key], section.currency)}` : formatAmount(section.totals[col.key], section.currency, false) }}
                    </td>
                  </tr>
                </tbody>
                <!-- per-section empty: keeps the column headers above, message spans the table -->
                <tbody v-else>
                  <tr class="empty-row">
                    <td :colspan="columns.length + 1">
                      <div class="d-flex flex-column align-center text-center my-4">
                        <!-- "no documents" mark: blue-500 circle with a crossed-out document glyph -->
                        <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" class="mb-2">
                          <rect width="30" height="30" rx="15" fill="#0352CC" />
                          <path d="M21.5 21.5929L20.7138 22.5L19.6367 21.2643C19.6181 21.2643 19.5933 21.2643 19.5748 21.2643H10.9081C10.2271 21.2643 9.67 20.6286 9.67 19.8357V9.83571C9.67 9.81429 9.67 9.78571 9.67 9.76429L8.5 8.40714L9.29238 7.5L20.6271 20.5786V20.5857L21.5 21.5929ZM20.8129 18.9786L11.651 8.40714H17.0986L20.8129 12.6929V18.9786ZM19.8843 13.4071L16.4795 9.47857V13.4071H19.8843Z" fill="white" />
                        </svg>
                        <span class="d-block subtitle-2 grey-700--text font-weight-bold">{{ section.emptyTitle }}</span>
                        <span class="d-block body-2 grey-500--text">{{ section.emptyDescription }}</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-col>
    </template>
  </v-row>
</template>
<script>
import MiniHeader from '@/components/commons/MiniHeader'
import EmptyState from '@/components/commons/VEmptyState'
import moment from 'moment'

export default {
  components: {
    MiniHeader,
    EmptyState
  },
  data: () => ({
    selected: {
      period: ''
    },
    // local labels keyed by code; resilient to backend encoding and missing labels
    documentTypeLabels: {
      '01': 'Factura',
      '03': 'Boleta de Venta',
      '07': 'Nota de Crédito',
      '08': 'Nota de Débito'
    },
    columns: [
      { key: 'totalDocuments', label: 'Cantidad', amount: false },
      { key: 'taxableAmount', label: 'Base imponible', amount: true },
      { key: 'igvTotal', label: 'Total del IGV', amount: true },
      { key: 'exemptAmount', label: 'Total op. exo.', amount: true },
      { key: 'unaffectedAmount', label: 'Total op. ina.', amount: true },
      { key: 'otherTaxesAmount', label: 'Otros importes', amount: true },
      { key: 'grandTotal', label: 'Total CP', amount: true }
    ],
    sectionDefs: [
      { key: 'ACCEPTED', title: 'Documentos aceptados', icon: 'mdi-check', chipColor: 'blue-50', chipTextColor: 'blue-500', emptyTitle: 'Sin documentos aceptados', emptyDescription: 'No se registraron documentos aceptados para el período seleccionado.' },
      { key: 'REJECTED', title: 'Documentos rechazados', icon: 'mdi-close', chipColor: 'red-100', chipTextColor: 'red-500', emptyTitle: 'Sin documentos rechazados', emptyDescription: 'No se registraron rechazos para el período seleccionado.' }
    ],
    report: null,
    dateMenu: false,
    loading: false,
    error: false,
    scroll: 0
  }),
  computed: {
    breadcrumbs () {
      return {
        main: 'Métricas',
        children: [
          { text: 'Reporte de ventas' }
        ]
      }
    },
    maxMonth () {
      return moment().format('YYYY-MM')
    },
    periodLabel () {
      if (!this.selected.period) return ''
      const label = moment(this.selected.period, 'YYYY-MM').format('MMMM [de] YYYY')
      return label.charAt(0).toUpperCase() + label.slice(1)
    },
    currencyGroups () {
      return this.report?.currencyGroups ?? []
    },
    cacheUpdatedLabel () {
      const updatedAt = this.report?.lastCacheUpdate
      if (!updatedAt) return ''
      // keep the backend offset (e.g. -05:00 Lima) instead of converting to browser-local time
      const m = moment.parseZone(updatedAt)
      const time = m.format('HH:mm')
      const isToday = m.format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')
      return isToday
        ? `Actualizado hoy a las ${time} hrs`
        : `Actualizado el ${m.format('DD-MM-YYYY')} a las ${time} hrs`
    },
    sections () {
      // one card per (currency × section): PEN aceptados/rechazados, USD aceptados/rechazados
      return this.currencyGroups.flatMap(group =>
        this.sectionDefs.map(def => {
          const section = group.sections?.[def.key] ?? {}
          const rows = section.documentTypes ?? []
          const totals = section.totals ?? {}
          return {
            ...def,
            currency: group.currency,
            key: `${group.currency}-${def.key}`,
            rows,
            totals,
            totalDocuments: totals.totalDocuments ?? 0,
            hasData: (totals.totalDocuments ?? 0) > 0
          }
        })
      )
    }
  },
  created () {
    // feature is Peru-only; block direct URL access for other countries
    if (this.$store.state.auth.account.country !== 'PE') {
      this.$router.replace({ name: 'AccountDashboard' }).catch(() => {})
      return
    }
    if (this.$route.query.period) {
      this.selected.period = this.$route.query.period
      this.getData()
    }
  },
  methods: {
    onScroll (e) {
      this.scroll = e.target?.scrollingElement?.scrollTop ?? 0
    },
    // map the backend snake_case payload to the camelCase shape used by the view
    normalizeReport (data) {
      if (!data) return null
      return {
        year: data.year,
        month: data.month,
        lastCacheUpdate: data.last_cache_update,
        currencyGroups: (data.currency_groups ?? []).map(g => ({
          currency: g.currency,
          sections: this.normalizeSections(g.sections)
        }))
      }
    },
    normalizeSections (sections) {
      return ['ACCEPTED', 'REJECTED'].reduce((acc, key) => {
        const section = sections?.[key] ?? {}
        acc[key] = {
          documentTypes: (section.document_types ?? []).map(this.normalizeRow),
          totals: this.normalizeRow(section.totals ?? {})
        }
        return acc
      }, {})
    },
    normalizeRow (row) {
      return {
        documentType: row.document_type,
        documentTypeLabel: row.document_type_label,
        totalDocuments: Number(row.total_documents) || 0,
        taxableAmount: Number(row.taxable_amount) || 0,
        igvTotal: Number(row.igv_total) || 0,
        exemptAmount: Number(row.exempt_amount) || 0,
        unaffectedAmount: Number(row.unaffected_amount) || 0,
        otherTaxesAmount: Number(row.other_taxes_amount) || 0,
        grandTotal: Number(row.grand_total) || 0
      }
    },
    symbolFor (currency) {
      return currency === 'PEN' ? 'S/.' : '$'
    },
    // format per currency: PEN uses dot-thousands/comma-decimal, USD uses comma-thousands/dot-decimal
    // local to this view so the app-wide numeral config (formatNumber2) stays untouched
    formatAmount (value, currency, withDecimals = true) {
      const isPEN = currency === 'PEN'
      const thousandsSep = isPEN ? '.' : ','
      const decimalSep = isPEN ? ',' : '.'
      const [intPart, decPart] = (Number(value) || 0).toFixed(withDecimals ? 2 : 0).split('.')
      const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep)
      return decPart ? `${grouped}${decimalSep}${decPart}` : grouped
    },
    docLabel (row) {
      return this.documentTypeLabels[row.documentType] || row.documentTypeLabel || row.documentType
    },
    onPeriodChange () {
      this.dateMenu = false
      this.$router.replace({ query: { period: this.selected.period } }).catch(() => {})
      this.getData()
    },
    getData () {
      if (!this.selected.period) return
      const [year, month] = this.selected.period.split('-')
      this.loading = true
      this.error = false
      this.report = null
      this.$store.dispatch('branches/GET', {
        resource: 'metrics/sales-report',
        query: {
          country: 'PE',
          year: Number(year),
          month: Number(month),
          ...(this.$store.state.auth.account.time_zone && { timezone: this.$store.state.auth.account.time_zone })
        }
      })
        .then(response => {
          this.report = this.normalizeReport(response.data)
        })
        .catch(() => {
          this.error = true
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>
<style lang="scss" scoped>
// semibold prefix ("Fecha") with a small gap before the value; Vuetify renders the prefix internally
.period-field ::v-deep .v-text-field__prefix {
  font-weight: 600;
  margin-right: 6px;
}
// keep the Total row shaded even on hover (table uses `no-hover`, which forces transparent on tr:hover)
::v-deep tr.total-row td {
  background-color: var(--v-grey-background-base) !important;
}
// in the empty state there are no data rows, so restore the column-header bottom border (Figma)
::v-deep .empty-table.v-data-table thead th {
  border-bottom: 1px solid var(--v-grey-50-base) !important;
}
</style>
